import { Grid, Header, Icon } from "semantic-ui-react";
import React, { useContext } from "react";
import { MathUtils, StringUtils } from "utils";
import { useDispatch, useSelector } from "react-redux";
import orderActions from "actions/order";
import { QuoteBuilderContext } from "shared/context/QuoteBuilderProvider";

const QuoteTotals = ({ quote, isInvoice, cart, disabled, account_id, wereTaxesCalculated, finishTaxCalculation, shouldNotDisplayPriceChanges, masterPricingSchedule = null }) => {
    const { fund_type } = useSelector(
        (state) => isInvoice ? state.order.order?.account || {} : {}
    );
    const { quoteBuilderConfig } = useContext(QuoteBuilderContext);
    const { id: order_id } = quoteBuilderConfig;
    const isDyl = useSelector(state => state.auth.dyl_employee);
    const additionalTaxes = useSelector(state => !disabled ? state.order.additionalTaxes : state.order.order?.tax || 0);
    const isCalculatingTaxes = useSelector(state => state.order.isCalculatingTaxes);
    const dispatch = useDispatch();
    const itemsInCart = cart.filter(item => !Boolean(item.removed));
    const pricing_schedule = itemsInCart.filter(item => item.pricing_schedule && item.dyl_product_name !== 'sms_balance')[0]?.pricing_schedule;
    const calculateTaxes = async () => {
        const itemsInCart = cart.filter(item => !Boolean(item.removed));
        dispatch(orderActions.calculateTaxes({
            price_schedule: pricing_schedule,
            products: itemsInCart.map(item => ({
                product_id: item.id,
                quantity: Number(item.quantity)
            }))
        }, { account_id }, order_id)).then(() => {
            finishTaxCalculation();
        });
    }
    const isEvaluationAccount = ["demo","trial"].includes(fund_type);
    const { grossTotal, totalDiscount, totalTax, totalFee, subtotal } = isEvaluationAccount
        ? {
            grossTotal: 0,
            totalDiscount: 0,
            totalTax: 0,
            totalFee: 0,
            subtotal: 0,
        }
        : cart
            .filter((item) => !Boolean(item.removed))
            .reduce(
                (a, item) => {
                    const productDetails = quote.quote_summary.find(
                        (quoteItem) =>
                            quoteItem.product_id === item.id &&
                            quoteItem.product_variation_id === item.variation_id
                    );
                    const price_data = Object.keys(productDetails.price_data).length ? productDetails.price_data : null;
                    const price = (() => {
                        if (productDetails.dyl_product_name?.includes("_balance")) {
                            return item.amount || 0;
                        }
                        if (!price_data || shouldNotDisplayPriceChanges) {
                            return productDetails.item_price || 0;
                        }
                        const isOneTime =
                            !price_data?.model?.includes(
                                "recurring"
                            );
                        const pricingModel = price_data.model;
                        if (isOneTime) {
                            if (!pricingModel.includes("volume")) {
                                return pricingModel === "usage"
                                    ? price_data.price.price
                                    : price_data.price;
                            }
                            return (
                                price_data.price.find(
                                    (range) =>
                                        item.quantity >= range.start &&
                                        item.quantity <=
                                        (range.end || Number.POSITIVE_INFINITY)
                                )?.price || 0
                            );
                        }
                        const price_schedule = price_data.price[masterPricingSchedule || item.pricing_schedule];
                        if (!pricingModel.includes("volume")) {
                            return (
                                productDetails?.prorate_amount || price_schedule?.price || 0
                            );
                        }
                        const frequency = price_schedule?.volumes || [];
                        const volumeTier = frequency.find(
                            (range) => item.quantity >= range.start &&
                                item.quantity <=
                                (range.end || Number.POSITIVE_INFINITY)
                        );
                        return (
                            productDetails?.prorate_amount || volumeTier?.price || 0
                        );
                    })();

                    const {
                        totalTax,
                        totalFee,
                        grossTotal,
                        subtotal,
                        discountAmount,
                    } = MathUtils.calculateItemTotals({
                        price,
                        additional_price:
                            productDetails.product_additional_price || 0,
                        quantity: item.quantity,
                        discount: item.discount || 0,
                        addons: productDetails.addon
                            ?.filter(
                                (addon) =>
                                    item.addons.includes(addon.id) && !addon.unlinked
                            )
                            .map((addon) => ({
                                price: addon.current_price || 0,
                            })) || [],
                        taxes: productDetails.tax?.map((tax) => ({
                            amount: tax.current_amount || tax.amount,
                            percent: tax.tax_fee_percent,
                        })) || [],
                        fees: productDetails.fee?.map((fee) => ({
                            amount: fee.current_amount || fee.amount,
                            percent: fee.tax_fee_percent,
                        })) || [],
                    });

                    return {
                        grossTotal: a.grossTotal + grossTotal,
                        totalDiscount: a.totalDiscount + discountAmount,
                        totalTax: a.totalTax + totalTax,
                        totalFee: a.totalFee + totalFee,
                        subtotal: a.subtotal + subtotal,
                    };
                },
                {
                    grossTotal: 0,
                    totalDiscount: 0,
                    totalTax: 0,
                    totalFee: 0,
                    subtotal: 0,
                }
            );

    return (
        <Grid divided="vertically">
            <Grid.Row>
                <Grid.Column>
                    <Header>Today's {isInvoice ? "Invoice" : "Quote"}</Header>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row columns="2">
                <Grid.Column>
                    <Header size="small">Total</Header>
                </Grid.Column>
                <Grid.Column textAlign="right">
                    <Header color="primary" size="small">
                        $
                        {StringUtils.formatDecimal(
                            `${MathUtils.roundDecimals(grossTotal)}`
                        , true)}
                    </Header>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column>
                    <Grid>
                        <Grid.Row columns="2">
                            <Grid.Column>
                                <Header size="small">Discount</Header>
                            </Grid.Column>
                            <Grid.Column textAlign="right">
                                <Header color="primary" size="small">
                                    -${StringUtils.formatDecimal(
                                        `${MathUtils.roundDecimals(totalDiscount)}`
                                    , true)}
                                </Header>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row columns="2">
                            <Grid.Column>
                                <Header size="small">Total Fee</Header>
                            </Grid.Column>
                            <Grid.Column textAlign="right">
                                <Header color="primary" size="small">
                                    $
                                    {StringUtils.formatDecimal(
                                        `${MathUtils.roundDecimals(totalFee)}`
                                    , true)}
                                </Header>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row columns="2">
                            <Grid.Column>
                                <Header size="small">Total Tax</Header>
                            </Grid.Column>
                            <Grid.Column textAlign="right">
                                <Header color="primary" size="small">
                                    {isDyl && !disabled && isInvoice && pricing_schedule && (
                                        <Icon link className="fas fa-arrow-rotate-left" onClick={calculateTaxes} loading={isCalculatingTaxes} disabled={wereTaxesCalculated} />
                                    )}
                                    $
                                    {StringUtils.formatDecimal(
                                        `${MathUtils.roundDecimals(totalTax + additionalTaxes)}`
                                    , true)}
                                </Header>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row columns="2">
                <Grid.Column>
                    <Header size="small">Subtotal</Header>
                </Grid.Column>
                <Grid.Column textAlign="right">
                    <Header color="primary" size="small">
                        $
                        {StringUtils.formatDecimal(
                            `${MathUtils.roundDecimals(subtotal + additionalTaxes)}`
                        , true)}
                    </Header>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

export default QuoteTotals;
